import type {
    CatalogItem as CatalogItemApi,
    CatalogItemFull,
} from "@albertine/types/openapi/lmt";
import { APIProvider, Map as GoogleMap, useMap } from "@vis.gl/react-google-maps";
import { PreviewCard } from "./PreviewCard";
import "./MapView.css";
import { useEffect, useRef, useState } from "react";
import type { Request } from "@/common/types/Request";
import Button from "@/components/Common/Buttons";
import Icon from "@/components/Common/Icon";
import { useCatalogSearchUrlParams } from "@/utils/customHook.util";
import { centerOfBounds, getSearchLocation } from "../distanceOrigin.util";
import { useCatalogSearchResults } from "../hooks/useCatalogSearchResults";
import { ClusteredMapPins } from "./ClusteredMapPins";
import type { MapPinDataArray } from "./MapPinData";
import { type MapBounds, useMapBounds } from "./useMapBounds";

type MapViewProps = {
    selectedItem: CatalogItemFull | undefined;
    request: Request | undefined;
    cartItems: string[];
    onCartItemClick: (item: CatalogItemFull) => void;
    onPinHover: (pinId: string | undefined) => void;
    highlightedItemId: string | undefined;
};

export function MapView({
    selectedItem,
    request,
    cartItems,
    onCartItemClick,
    onPinHover,
    highlightedItemId,
}: MapViewProps) {
    const { urlState } = useCatalogSearchUrlParams();
    const { data: searchResults, isLoading } = useCatalogSearchResults(urlState.filter);
    const allResults = (searchResults?.pages ?? []).flatMap((page) => page.data);
    const mapPinData = mapToPinData(allResults);
    const [draggedAfterSearch, setDraggedAfterSearch] = useState(false);
    const mapBoundsInitialized = useRef(false);
    const { center, bounds } = useMapBounds(mapPinData);

    // We use the actual bounds in the _viewport_ rather than "center" from useMapBounds.
    // The "center" is the center of the pins, which is not always the center of the viewport.
    const boundsCenter = centerOfBounds(urlState.filter);
    const searchLocation = getSearchLocation(urlState.filter);

    const distanceDestination = boundsCenter ?? searchLocation ?? center ?? undefined;
    const [initialBounds, setInitialBounds] = useState<MapBounds | undefined>(
        undefined,
    );

    useEffect(
        function resetMapBoundsInitialized() {
            mapBoundsInitialized.current = false;

            if (isLoading) return;

            setDraggedAfterSearch(false);
        },
        [isLoading],
    );

    useEffect(
        function setInitialBoundsOnMount() {
            if (initialBounds) return;
            if (!center && !bounds) return;

            setInitialBounds({ center, bounds });
        },
        [center, bounds, initialBounds],
    );

    return (
        <APIProvider apiKey={import.meta.env.REACT_APP_MAPS_PLATFORM_API_KEY}>
            <div className="catalog__map-view">
                {draggedAfterSearch && <SearchAreaButton />}

                {selectedItem && (
                    <PreviewCard
                        item={selectedItem}
                        request={request}
                        cartItems={cartItems}
                        onCartItemClick={onCartItemClick}
                        mapCenter={distanceDestination}
                    />
                )}

                <MapWrapper selectedItem={selectedItem}>
                    {initialBounds && (
                        <GoogleMap
                            style={{ width: "100%", height: "100%" }}
                            defaultCenter={initialBounds?.center}
                            defaultBounds={initialBounds?.bounds}
                            defaultZoom={
                                !initialBounds?.bounds
                                    ? SINGLE_ITEM_ZOOM_LEVEL
                                    : undefined
                            }
                            gestureHandling={"greedy"}
                            disableDefaultUI
                            mapId="catalog-map-view"
                            onCameraChanged={() => {
                                if (!mapBoundsInitialized.current) {
                                    mapBoundsInitialized.current = true;
                                    return;
                                }

                                if (draggedAfterSearch) return;

                                setDraggedAfterSearch(true);
                            }}
                        >
                            <ClusteredMapPins
                                pins={mapPinData}
                                highlightedPinId={highlightedItemId}
                                onPinHover={onPinHover}
                            />
                        </GoogleMap>
                    )}
                </MapWrapper>
            </div>
        </APIProvider>
    );
}

function mapToPinData(searchResults: CatalogItemApi[]): MapPinDataArray {
    return searchResults.map((result) => ({
        id: result.lmtId,
        lat: result.locationGeoPoint?.latitude ?? 0,
        lng: result.locationGeoPoint?.longitude ?? 0,
        name: result.name,
    }));
}

function SearchAreaButton() {
    const map = useMap();
    const { setUrlState, urlState } = useCatalogSearchUrlParams();

    const { isFetching } = useCatalogSearchResults(urlState.filter, { enabled: false });

    function searchWithBounds() {
        const bounds = map?.getBounds()?.toJSON();

        if (bounds) {
            setUrlState({
                ...urlState,
                filter: {
                    ...urlState.filter,
                    geoBoundingBoxTop: bounds.north,
                    geoBoundingBoxBottom: bounds.south,
                    geoBoundingBoxLeft: bounds.west,
                    geoBoundingBoxRight: bounds.east,
                },
            });
        }
    }

    return (
        <Button.N10
            onClick={searchWithBounds}
            className="catalog__map-view-search-button"
            showLoadingSpinner={isFetching}
        >
            <Icon.Search.Small />
            Search this area
        </Button.N10>
    );
}

const SINGLE_ITEM_ZOOM_LEVEL = 13;

function MapWrapper({
    children,
    selectedItem,
}: {
    children: React.ReactNode;
    selectedItem: CatalogItemApi | undefined;
}) {
    const map = useMap();

    useEffect(
        function panToSelectedItem() {
            if (!map) return;
            if (!selectedItem?.locationGeoPoint) return;

            map.panTo({
                lat: selectedItem.locationGeoPoint.latitude,
                lng: selectedItem.locationGeoPoint.longitude,
            });

            const currentZoom = map.getZoom();

            if (currentZoom && currentZoom < SINGLE_PIN_ZOOM_LEVEL) {
                map.setZoom(SINGLE_PIN_ZOOM_LEVEL);
            }
        },
        [selectedItem, map],
    );

    return children;
}

const SINGLE_PIN_ZOOM_LEVEL = 17;
